/**
 * Session guard — one place that watches every call the browser makes to
 * the server, installed once before the app renders (src/index.js).
 *
 * 1. A call made while the tab is hidden carries the X-PCG-Background
 *    header. The server checks such a call for expiry but does not count it
 *    as activity, so a tab left open on a desk cannot keep a session alive
 *    past the 30-minute inactivity limit.
 *
 * 2. When the server answers that there is no session, the guard raises the
 *    pcg:session-gone event once. The app then returns to the sign-in screen
 *    (SessionWatch in App.js), which unmounts every poller, so a signed-out
 *    tab stops calling the server and the platform can idle.
 *
 * Only calls to this app's own /api are watched. Sign-in and other pre-session
 * calls under /api/auth never raise the event; a successful one resets it.
 */

export const SESSION_GONE_EVENT = 'pcg:session-gone';
export const BACKGROUND_HEADER = 'X-PCG-Background';

// Every body the server sends when there is no usable session (auth
// middleware, access helpers, SOC 2 inactivity check). Token and service
// errors deliberately are not here.
const SESSION_GONE_MESSAGES = new Set([
  'Authentication required',
  'Not authenticated',
  'Not authenticated.',
  'Not logged in',
  'No session.',
  'Session expired. Sign in again.',
  'Session expired. Please log in again.',
  'Please sign in again.',
]);

const API_BASE = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_URL) || '';

// Path under /api for a call to this app's own server, else null.
export function apiPath(input, win = typeof window !== 'undefined' ? window : null) {
  const url = typeof input === 'string' ? input : (input && typeof input.url === 'string' ? input.url : '');
  if (!url) return null;
  let path = url;
  if (API_BASE && url.startsWith(API_BASE)) {
    path = url.slice(API_BASE.length);
  } else if (/^https?:\/\//i.test(url)) {
    try {
      const u = new URL(url);
      if (!win || u.origin !== win.location.origin) return null;
      path = u.pathname;
    } catch { return null; }
  }
  return path.startsWith('/api/') ? path.split('?')[0] : null;
}

export function isSessionGoneBody(body) {
  if (!body || typeof body !== 'object') return false;
  if (body.code === 'SESSION_INACTIVE' || body.authenticated === false) return true;
  return typeof body.message === 'string' && SESSION_GONE_MESSAGES.has(body.message);
}

export function isBackground(doc = typeof document !== 'undefined' ? document : null) {
  return !!doc && doc.visibilityState === 'hidden';
}

export function installSessionGuard(win) {
  if (!win || typeof win.fetch !== 'function' || win.fetch.__pcgSessionGuard) return win && win.fetch;
  const realFetch = win.fetch.bind(win);
  let announced = false;

  const guarded = async function guardedFetch(input, init) {
    const path = apiPath(input, win);
    let opts = init;
    if (path && isBackground(win.document)) {
      const HeadersCtor = win.Headers || Headers;
      const headers = new HeadersCtor((init && init.headers) || (input && input.headers) || undefined);
      headers.set(BACKGROUND_HEADER, '1');
      opts = { ...(init || {}), headers };
    }
    const res = await realFetch(input, opts);
    if (!path) return res;
    if (path.startsWith('/api/auth/')) {
      if (res.ok) announced = false;
      return res;
    }
    if (res.status === 401 && !announced) {
      let body = null;
      try { body = await res.clone().json(); } catch { body = null; }
      if (isSessionGoneBody(body)) {
        announced = true;
        win.dispatchEvent(new Event(SESSION_GONE_EVENT));
      }
    }
    return res;
  };
  guarded.__pcgSessionGuard = true;
  win.fetch = guarded;
  return guarded;
}
